import React, { useEffect } from "react";
import { View } from "react-native";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { Box } from "@mui/material";
import { FormInput } from "common/components/form/hookForms/FormInput";
import { SmallFormNumberInput } from "common/components/form/hookForms/SmallFormNumberInput";
import {
  MyPropertyAddRequest,
  ValuationType,
} from "common/types/common.types";
import {
  DesignationType,
  getTransactionREType,
  REType,
} from "common/types/comparableFields.types";
import { getMaxErrorText, getMinErrorText } from "common/helpers/form.helpers";
import { showToastError } from "common/toast/toast";
import { getErrorMessage } from "common/helpers/error.helpers";
import { ContentCard } from "../../components/ContentCard";
import { CenteredContentWrapper } from "../../components/wrappers/CenteredContentWrapper";
import { MUILoadingButton } from "../../components/buttons/MUILoadingButton";
import { PropertyTypeSelector } from "../../components/propertyCRUD/PropertyTypeSelector";
import { addAppraiserProperty } from "../../api/appraiserProperties.api";
import { useAppraiser } from "../../hooks/auth.hooks";
import { AppraiserRole } from "../../types/appraiser.types";
import { useRouteQuery, useScrollToTop } from "../../hooks/router.hooks";
import { CityFormField } from "./addressInput/CityFormField";
import { StreetFormField } from "./addressInput/StreetFormField";
import { BuildingNumberField } from "./addressInput/BuildingNumberField";
import { MapField } from "./addressInput/MapField";

interface PropertySimpleCreateRouteProps {}

interface SimplePropertyFormValues {
  designationType: DesignationType;
  city: string;
  cityId: number;
  street: string;
  streetId: number;
  streetNumber: string;
  address: string;
  label: string;
  lat: number;
  lng: number;
  area: number;
  rooms: number;
  floorNr?: number | null;
  floorCount: number;
  plotArea?: number | null;
  year: number;
}

const minRooms = 1;
const maxRooms = 10;
const minFloor = 0;
const maxFloor = 100;
const minArea = 1;
const maxArea = 1000;
const minBuildYear = 1900;
const maxBuildYear = new Date().getFullYear() + 3;

const isHouse = (designationType: any) =>
  getTransactionREType(designationType) === REType.House;

const validationSchema: Yup.ObjectSchema<SimplePropertyFormValues> = Yup.object({
  designationType: Yup.mixed<DesignationType>()
    .oneOf(Object.values(DesignationType))
    .required(),
  city: Yup.string().required(),
  cityId: Yup.number().required(),
  street: Yup.string().required(),
  streetId: Yup.number().required(),
  streetNumber: Yup.string().required(),
  address: Yup.string().required(),
  label: Yup.string().required(),
  lat: Yup.number().required(),
  lng: Yup.number().required(),
  area: Yup.number()
    .required()
    .min(minArea, getMinErrorText(i18n.t("propertyForm.areaLabel"), minArea))
    .max(maxArea, getMaxErrorText(i18n.t("propertyForm.areaLabel"), maxArea)),
  rooms: Yup.number()
    .required()
    .min(minRooms, getMinErrorText(i18n.t("propertyForm.roomsLabel"), minRooms))
    .max(
      maxRooms,
      getMaxErrorText(i18n.t("propertyForm.roomsLabel"), maxRooms)
    ),
  floorNr: Yup.number().when("designationType", {
    is: (d: any) => !isHouse(d),
    then: (schema) =>
      schema
        .required()
        .min(
          minFloor,
          getMinErrorText(i18n.t("propertyForm.floorLabel"), minFloor)
        )
        .max(
          maxFloor,
          getMaxErrorText(i18n.t("propertyForm.floorLabel"), maxFloor)
        ),
    otherwise: (schema) => schema.nullable(),
  }),
  floorCount: Yup.number()
    .required()
    .min(
      minFloor + 1,
      getMinErrorText(i18n.t("propertyForm.floorCountLabel"), minFloor + 1)
    )
    .max(
      maxFloor,
      getMaxErrorText(i18n.t("propertyForm.floorCountLabel"), maxFloor)
    ),
  plotArea: Yup.number().when("designationType", {
    is: isHouse,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  year: Yup.number()
    .required()
    .min(
      minBuildYear,
      getMinErrorText(i18n.t("propertyForm.buildYearLabel"), minBuildYear)
    )
    .max(
      maxBuildYear,
      getMaxErrorText(i18n.t("propertyForm.buildYearLabel"), maxBuildYear)
    ),
});

export const PropertySimpleCreateRoute: React.FC<
  PropertySimpleCreateRouteProps
> = () => {
  const { t } = useTranslation();
  useScrollToTop();

  const navigate = useNavigate();
  const query = useRouteQuery();
  const appraiserId = query.get("appraiserId") ?? undefined;
  const isLocationCheck =
    query.get("valuationType") === ValuationType.LocationCheck;
  const { data: appraiser, isLoading: isAppraiserLoading } = useAppraiser();

  const gapHeight = 30;

  const methods = useForm<SimplePropertyFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      designationType: DesignationType.ResidentialApartments,
    },
  });

  const designationType = methods.watch("designationType");
  const city = methods.watch("city");
  const street = methods.watch("street");
  const streetNumber = methods.watch("streetNumber");

  useEffect(() => {
    if (!city || !street || !streetNumber) {
      methods.setValue("address", "");
      methods.setValue("label", "");
      return;
    }
    const address = `${street} ${streetNumber}, ${city}`;
    methods.setValue("address", address);
    methods.setValue("label", address);
  }, [city, street, streetNumber]);

  useEffect(() => {
    if (isHouse(designationType)) {
      methods.setValue("floorNr", null);
    } else {
      methods.setValue("plotArea", null);
    }
  }, [designationType]);

  const getValuationType = () => {
    if (isLocationCheck) {
      return ValuationType.LocationCheck;
    }
    if (
      appraiser?.role === AppraiserRole.ADMIN ||
      appraiser?.role === AppraiserRole.APPRAISER
    ) {
      return ValuationType.PriceCheck;
    }
    return ValuationType.FullReport;
  };

  const { mutate, isLoading } = useMutation(
    (values: SimplePropertyFormValues) => {
      const property: MyPropertyAddRequest = {
        ...values,
        floorNr: isHouse(values.designationType) ? null : values.floorNr,
        plotArea: isHouse(values.designationType) ? values.plotArea : null,
        // Fields the simple flow does not ask for, but the valuation requires.
        isRenovated: false,
        finishing: true,
        finishingFull: 100,
        registeredFinishingFull: 100,
        condition: 3,
        valuationType: getValuationType(),
      };

      return addAppraiserProperty(property, false, appraiserId);
    },
    {
      onSuccess: (property) => {
        navigate(`/properties/${property._id}`);
      },
      onError: (error) => {
        showToastError(getErrorMessage(error));
      },
    }
  );

  if (isAppraiserLoading) {
    return null;
  }

  return (
    <div>
      <ContentCard title={undefined} style={{ marginBottom: "20px" }}>
        <PropertyTypeSelector
          selectedType={designationType}
          onChange={(type) => methods.setValue("designationType", type)}
        />
      </ContentCard>
      <ContentCard title={t("propertyCreation.simple.title")}>
        <Box
          sx={{
            width: { xs: "100%", sm: "450px", md: "500px" },
            mx: "auto",
          }}
        >
          <FormProvider {...methods}>
            <CityFormField />
            <View style={{ height: gapHeight }} />
            <StreetFormField />
            <View style={{ height: gapHeight }} />
            <BuildingNumberField />
            <View style={{ height: gapHeight }} />
            <MapField />
            <View style={{ height: gapHeight }} />
            <FormInput
              units={t("property.units.sqm")}
              label={t("propertyForm.areaLabel")}
              name="area"
              isRequired
              variant="decimal"
            />
            <View style={{ height: gapHeight }} />
            <SmallFormNumberInput
              minValue={minRooms}
              maxValue={maxRooms}
              label={t("propertyForm.roomsLabel")}
              name="rooms"
              isRequired
            />
            <View style={{ height: gapHeight }} />
            <View
              style={{ flexDirection: "row", justifyContent: "space-between" }}
            >
              {!isHouse(designationType) && (
                <View style={{ flex: 1, marginRight: 15 }}>
                  <SmallFormNumberInput
                    minValue={minFloor}
                    maxValue={maxFloor}
                    label={t("propertyForm.floorLabel")}
                    name="floorNr"
                    isRequired
                  />
                </View>
              )}
              <View style={{ flex: 1 }}>
                <SmallFormNumberInput
                  minValue={minFloor + 1}
                  maxValue={maxFloor}
                  label={t("propertyForm.floorCountLabel")}
                  name="floorCount"
                  isRequired
                />
              </View>
            </View>
            <View style={{ height: gapHeight }} />
            {isHouse(designationType) && (
              <>
                <FormInput
                  units="a"
                  label={t("propertyForm.plotAreaLabel")}
                  name="plotArea"
                  isRequired
                  variant="decimal"
                />
                <View style={{ height: gapHeight }} />
              </>
            )}
            <FormInput
              name="year"
              label={t("propertyForm.buildYearLabel")}
              isRequired
              variant="number"
            />
          </FormProvider>
        </Box>
      </ContentCard>
      <CenteredContentWrapper style={{ marginTop: "20px" }}>
        <MUILoadingButton
          loading={isLoading}
          sx={{ width: "250px" }}
          onClick={methods.handleSubmit((values) => mutate(values))}
        >
          {t("propertyCreation.simple.create")}
        </MUILoadingButton>
      </CenteredContentWrapper>
    </div>
  );
};
